"use client";

import { useEffect, useRef, useState } from "react";
import ScoreDisplay from "@/components/ScoreDisplay";
import SpinningWheel from "@/components/SpinningWheel";
import ResultsSummary from "@/components/ResultsSummary";
import ChaosControl from "@/components/ChaosControl";
import ChaosWheelOverlay from "@/components/ChaosWheelOverlay";
import CrownOverlay from "@/components/CrownOverlay";
import ChaosEffectsPanel from "@/components/ChaosEffectsPanel";
import { useGameState } from "@/hooks/useGameState";
import { useFireSound } from "@/hooks/useFireSound";
import { playChaosSound, playTick, unlockAudio } from "@/lib/sounds";

interface DemoRunProps {
  // Leave the demo and return to the welcome screen ("Play for Real").
  onExit: () => void;
}

// A throwaway "for fun" run: the full game (same wheels, RNG, chaos, crown,
// scoring) against a SEPARATE localStorage key with no Supabase, no tokens, no
// cooldown and no leaderboard. It deliberately mirrors page.tsx's in-game
// orchestration rather than sharing it, so demo can never perturb the real
// solo / multiplayer flow.
export default function DemoRun({ onExit }: DemoRunProps) {
  const {
    phase,
    currentWheel,
    showValue,
    wheelNumber,
    totalWheels,
    isLastStep,
    results,
    pendingOption,
    spinning,
    showResult,
    spinToken,
    spin,
    handleSpinEnd,
    next,
    reset,
    totalScore,
    rank,
    alignmentLabel,
    leaderboardBoard,
    isSidekick,
    chaosResults,
    chaosEffects,
    chaosCooldownLabel,
    canTriggerChaos,
    chaosOutcome,
    chaosOverlayStage,
    chaosPending,
    chaosTypeSpinToken,
    chaosResultSpinToken,
    triggerChaosManually,
    handleChaosTypeSpinEnd,
    handleChaosResultSpinEnd,
    dismissChaosOverlay,
    fightResults,
    fightStep,
    startFight,
    hasCrown,
    crownStage,
    crownSpinToken,
    crownPendingOption,
    crownBonusOption,
    takeCrown,
    spinCrownWheel,
    handleCrownSpinEnd,
    continueAfterCrown,
    submittedScoreId,
    cooldownStartedAt,
    heatLevel,
  } = useGameState({ demo: true });

  // Same wheel-fire audio as the real game.
  useFireSound(heatLevel);

  let nextLabel = "Next Wheel";
  if (phase === "alignment" && pendingOption?.name === "Sidekick") {
    nextLabel = "Spin Again";
  } else if (phase === "fight") {
    nextLabel = fightStep === "outcome" ? "Back to Results" : "Reveal Outcome";
  } else if (isLastStep) {
    nextLabel = "See Results";
  }

  const handleSpin = () => {
    unlockAudio();
    spin();
  };

  // Chaos announce sound, same as the real game.
  const prevChaosAnnounce = useRef(false);
  useEffect(() => {
    const announcing = chaosOverlayStage === "type";
    if (announcing && !prevChaosAnnounce.current) {
      playChaosSound();
    }
    prevChaosAnnounce.current = announcing;
  }, [chaosOverlayStage]);

  // 2s cooldown after a result lands before "advance" unlocks.
  const [cooldownElapsed, setCooldownElapsed] = useState(false);
  const [countdown, setCountdown] = useState(2);
  useEffect(() => {
    if (!showResult) {
      setCooldownElapsed(false);
      setCountdown(2);
      return;
    }
    setCooldownElapsed(false);
    setCountdown(2);
    const start = Date.now();
    const interval = setInterval(() => {
      const remaining = Math.ceil((2000 - (Date.now() - start)) / 1000);
      if (remaining <= 0) {
        setCountdown(0);
        setCooldownElapsed(true);
        clearInterval(interval);
      } else {
        setCountdown(remaining);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [showResult]);

  const chaosBusy = chaosPending || chaosOverlayStage !== null;
  const advanceReady = cooldownElapsed && !chaosBusy;

  const spinButton = {
    label: spinning ? "..." : "Spin",
    onClick: handleSpin,
    disabled: spinning || showResult,
  };

  const advanceFooter = showResult ? (
    <button
      onClick={next}
      disabled={!advanceReady}
      className="animate-slide-down min-w-[10rem] rounded-full bg-accent px-[clamp(1.75rem,3.5vw,2.5rem)] py-[clamp(0.5rem,1.5vh,0.75rem)] text-[clamp(0.9rem,1.8vw,1.125rem)] font-bold uppercase tracking-widest text-white shadow-[0_0_30px_rgba(124,58,237,0.5)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {advanceReady ? nextLabel : cooldownElapsed ? "..." : `${countdown}`}
    </button>
  ) : null;

  return (
    <main className="flex min-h-[100dvh] flex-col items-center">
      {/* Persistent demo banner — amber, distinct from the normal UI, always
          visible (sticky) so the player never forgets they're in a demo. */}
      <div className="sticky top-0 z-40 flex h-9 w-full items-center justify-center gap-3 border-b border-amber-400/40 bg-amber-500/15 px-3 text-center backdrop-blur">
        <span className="text-[0.6rem] font-bold uppercase tracking-[0.2em] text-amber-300 sm:text-xs">
          ⚠ Demo Run — Results cannot be submitted to the leaderboard
        </span>
        <button
          onClick={onExit}
          className="shrink-0 rounded-full border border-amber-400/50 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-widest text-amber-300 transition hover:bg-amber-400/20"
        >
          Exit
        </button>
      </div>

      {phase !== "complete" && <ChaosEffectsPanel effects={chaosEffects} />}

      <ScoreDisplay
        score={totalScore}
        rank={rank}
        alignmentLabel={alignmentLabel}
        hasCrown={hasCrown}
        topClass="top-9"
      />

      <div className="flex w-full flex-1 flex-col items-center px-4 pb-24 pt-3 md:pb-28 md:pt-[clamp(1.25rem,4vh,2.5rem)]">
        {phase !== "complete" && currentWheel && (
          <>
            <div className="mb-3 text-center md:mb-[clamp(1rem,2.5vh,1.5rem)]">
              <div className="text-[clamp(0.7rem,1.3vw,0.875rem)] uppercase tracking-widest text-white/40">
                {phase === "alignment"
                  ? "Determine Your Alignment"
                  : phase === "fight"
                    ? "Bonus: Pick a Fight"
                    : `Wheel ${wheelNumber} of ${totalWheels}`}
              </div>
              <h1 className="mt-1 text-[clamp(1.5rem,3.5vw,2.25rem)] font-extrabold">
                {currentWheel.name}
              </h1>
            </div>

            <SpinningWheel
              wheel={currentWheel}
              spinToken={spinToken}
              resultOption={pendingOption}
              onSpinEnd={handleSpinEnd}
              onTick={playTick}
              spinButton={spinButton}
              footer={advanceFooter}
              showResult={showResult}
              showValue={showValue}
              heat={heatLevel}
            />

            {(phase === "main" || phase === "abilities") && (
              <ChaosControl
                cooldownLabel={chaosCooldownLabel}
                ready={canTriggerChaos}
                disabled={spinning || showResult || chaosBusy}
                onTrigger={triggerChaosManually}
              />
            )}
          </>
        )}

        {phase === "complete" && (
          <ResultsSummary
            results={results}
            chaosResults={chaosResults}
            fightResults={fightResults}
            onPickFight={startFight}
            onReroll={reset}
            onSubmitted={() => {}}
            submittedScoreId={submittedScoreId}
            totalScore={totalScore}
            rank={rank}
            alignmentLabel={alignmentLabel}
            leaderboardBoard={leaderboardBoard}
            isSidekick={isSidekick}
            hasCrown={hasCrown}
            cooldownStartedAt={cooldownStartedAt}
            demo
            onDemoRollAgain={reset}
            onDemoPlayForReal={onExit}
          />
        )}
      </div>

      <ChaosWheelOverlay
        stage={chaosOverlayStage}
        outcome={chaosOutcome}
        typeSpinToken={chaosTypeSpinToken}
        resultSpinToken={chaosResultSpinToken}
        onTypeSpinEnd={handleChaosTypeSpinEnd}
        onResultSpinEnd={handleChaosResultSpinEnd}
        onDismiss={dismissChaosOverlay}
        onTick={playTick}
      />

      <CrownOverlay
        stage={crownStage}
        spinToken={crownSpinToken}
        pendingOption={crownPendingOption}
        bonusOption={crownBonusOption}
        onTakeCrown={takeCrown}
        onSpin={spinCrownWheel}
        onSpinEnd={handleCrownSpinEnd}
        onContinue={continueAfterCrown}
        onTick={playTick}
      />
    </main>
  );
}
